import { useState, useEffect } from 'react'
import { TestProvider, useTest } from '../contexts/TestContext'
import { useAuth } from '../contexts/AuthContext'
import { useApi } from '../hooks/useApi'
import TestModeSelector from '../components/test/TestModeSelector'
import TestInterface from '../components/test/TestInterface'
import TestResults from '../components/test/TestResults'
import AuthModal from '../components/AuthModal'
import ShareModal from '../components/social/ShareModal'
import { PageTransition, LoadingTransition } from '../components/ui/Transitions'
import { ProgressLoadingOverlay, OfflineModeIndicator, NetworkErrorBanner, ConnectionStatus } from '../components/ui/LoadingStates'
import ErrorBoundary from '../components/ui/ErrorBoundary'

// Test Page Content Component (wrapped by TestProvider)
function TestPageContent() {
  const {
    status,
    mode,
    questions,
    currentQuestionIndex,
    answers,
    timeLeft,
    results,
    isLoading,
    error,
    isOnline,
    networkError,
    hasOfflineData,
    selectMode,
    startTest,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    jumpToQuestion,
    submitTest,
    resetTest,
    clearNetworkError
  } = useTest()

  const { user } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  // const { getUserProfile } = useApi() // Commented out for demo mode
  const [userProfile, setUserProfile] = useState(null)

  // Determine if user can submit to leaderboard (must be logged in)
  const canSubmitToLeaderboard = !!user

  // Load user profile on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // const profile = await getUserProfile() // Demo mode
        // setUserProfile(profile) // Demo mode
      } catch (err) {
        // User not logged in or error - continue as anonymous
        console.log('No user profile available:', err.message)
      }
    }
    
    // loadUserProfile() // Demo mode disabled
  }, []) // Removed getUserProfile dependency for demo mode

  // Show login modal when test is completed and user can't submit to leaderboard
  useEffect(() => {
    if (results && !canSubmitToLeaderboard) {
      const hideLoginPrompt = sessionStorage.getItem('hideLoginPrompt')
      if (hideLoginPrompt !== 'true') {
        setShowAuthModal(true)
      }
    }
  }, [results, canSubmitToLeaderboard])

  // Handle mode selection
  const handleModeSelection = async (selectedMode) => {
    selectMode(selectedMode)
    await startTest(selectedMode)
  }

  // Handle answer selection
  const handleAnswerSelect = (questionId, answerIndex) => {
    answerQuestion(questionId, answerIndex)
  }

  // Navigation functions
  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      nextQuestion()
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      previousQuestion()
    }
  }

  const handleSkipQuestion = () => {
    handleNextQuestion()
  }

  const handleJumpToQuestion = (index) => {
    jumpToQuestion(index)
  }

  // Submit test
  const handleSubmitTest = () => {
    submitTest()
  }

  // Handle actions
  const handleRetakeTest = () => {
    resetTest()
  }

  const handleShareResults = () => {
    if (!results) return
    setShowShareModal(true)
  }

  const handleViewDetailedAnalysis = () => {
    if (mode === 'comprehensive') {
      // Show detailed analysis
      alert('Detailed analysis feature coming soon!')
    } else {
      alert('Upgrade to comprehensive test for detailed analysis!')
    }
  }

  const handleAuthModalClose = () => {
    setShowAuthModal(false)
  }

  const handleDontShowAgainChange = (checked) => {
    if (checked) {
      sessionStorage.setItem('hideLoginPrompt', 'true')
    }
  }

  // Error display
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={resetTest}
            className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <PageTransition>
        <div className={`min-h-screen px-4 ${status === 'completed' ? 'py-4' : status === 'in_progress' ? 'py-6' : 'py-12'}`}>
          {/* Offline Mode Indicator */}
          {!isOnline && (
            <div className="mb-4">
              <OfflineModeIndicator />
            </div>
          )}

          {/* Network Error Banner */}
          {networkError && (
            <div className="mb-4">
              <NetworkErrorBanner
                error={networkError.message}
                onRetry={() => {
                  clearNetworkError()
                  window.location.reload()
                }}
                onDismiss={clearNetworkError}
              />
            </div>
          )}

          {/* Offline Data Recovery Notice */}
          {hasOfflineData && isOnline && (
            <div className="mb-4 p-4 bg-info-100 border border-info-200 rounded-lg">
              <p className="text-sm text-info-800">
                We found saved progress from when you were offline. Your test will continue where you left off.
              </p>
            </div>
          )}

          <LoadingTransition
            isLoading={status !== 'mode_selection'}
            fallback={<div />}
          >
            <TestModeSelector
              onSelectMode={handleModeSelection}
              userProfile={userProfile}
              isLoading={isLoading}
            />
          </LoadingTransition>
          
          <LoadingTransition
            isLoading={status !== 'in_progress' || questions.length === 0}
            fallback={
              <ProgressLoadingOverlay
                isLoading={isLoading}
                message={isOnline ? "Loading questions..." : "Loading offline questions..."}
                showProgress={false}
              />
            }
          >
            <TestInterface
              questions={questions}
              currentQuestionIndex={currentQuestionIndex}
              answers={answers}
              timeLeft={timeLeft}
              onAnswerSelect={handleAnswerSelect}
              onNextQuestion={handleNextQuestion}
              onPreviousQuestion={handlePreviousQuestion}
              onSkipQuestion={handleSkipQuestion}
              onSubmitTest={handleSubmitTest}
              onJumpToQuestion={handleJumpToQuestion}
              isLoading={isLoading}
            />
          </LoadingTransition>
          
          <LoadingTransition
            isLoading={status !== 'completed' || !results}
            fallback={
              <ProgressLoadingOverlay
                isLoading={isLoading}
                message="Calculating your results..."
                showProgress={false}
              />
            }
          >
            <TestResults
              results={results}
              testType={mode}
              onRetakeTest={handleRetakeTest}
              onShareResults={handleShareResults}
              onViewDetailedAnalysis={handleViewDetailedAnalysis}
            />
          </LoadingTransition>

          {/* Login Prompt Modal for Guest Users */}
          <AuthModal
            isOpen={showAuthModal}
            onClose={handleAuthModalClose}
            title="Save Your Score to the Leaderboard!"
            subtitle={`Log in to save your IQ score of ${results?.iqScore || ''} and compete with others on the leaderboard!`}
            showDontShowAgain={true}
            onDontShowAgainChange={handleDontShowAgainChange}
          />

          {/* Share Results Modal */}
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            result={results}
            userProfile={userProfile}
          />
        </div>
      </PageTransition>
    </ErrorBoundary>
  )
}

// Main TestPage component with provider
function TestPage() {
  return (
    <TestProvider>
      <TestPageContent />
    </TestProvider>
  )
}

export default TestPage
